import type {FC} from 'react';
import {usePathname} from 'next/navigation';

import {Button} from '@/components/button';
import {Link} from '@/components/link';
import {cn} from '@/utils/tailwind';

export type NavLinkType = {
	type: 'navlink';
	title: string;
	href: string;
	targetBlank?: boolean;
	featured?: boolean;
};

type NavLinkProps = {
	link: NavLinkType;
	onClick?: () => void;
	className?: string;
};

export const NavLink: FC<NavLinkProps> = ({link, onClick, className}) => {
	const pathname = usePathname();

	const isActive = pathname === link.href;

	if (link.featured) {
		return (
			<Button
				asChild
				size="medium"
				data-color="primary-light"
				className={cn(
					'max-lg:text-2xl',
					'font-display',
					'font-semibold',
					className,
				)}
			>
				<Link
					onClick={onClick}
					href={link.href}
					targetBlank={link.targetBlank}
				>
					{link.title}
				</Link>
			</Button>
		);
	}

	return (
		<Link
			onClick={onClick}
			href={link.href}
			targetBlank={link.targetBlank}
			className={cn(
				'relative',
				'inline-block',
				'no-underline',
				'transition-all',
				'max-lg:text-2xl',
				'text-primary-base-default',
				'hover:text-secondary-text-default',
				'font-display',
				'font-semibold',
				isActive
					? [
							'text-secondary-text-default',
							'after:bg-secondary-text-default',
							'after:absolute',
							'after:h-[3px]',
							'after:w-full',
							'after:-bottom-1',
							'max-lg:after:-bottom-2',
							'after:left-0',
							'after:right-0',
							'after:rounded-full',
						]
					: [
							'after:bg-secondary-text-default',
							'after:absolute',
							'after:h-[3px]',
							'after:w-full',
							'after:scale-x-0',
							'after:left-0',
							'after:rounded-full',
							'after:duration-100',
							'after:-bottom-1',
							'hover:after:scale-x-100',
							'after:origin-left',
						],
				className,
			)}
		>
			{link.title}
		</Link>
	);
};
